import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { POPULAR_CURRENCIES } from '@/utils/constants'
import { getCurrencySymbol } from '@/utils/formatters'

interface CurrencySelectorProps {
  value: string
  onChange: (currency: string) => void
  placeholder?: string
  disabled?: boolean
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select currency',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredCurrencies, setFilteredCurrencies] = useState([...POPULAR_CURRENCIES])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Filter currencies based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = POPULAR_CURRENCIES.filter(currency =>
        currency.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredCurrencies(filtered)
    } else {
      setFilteredCurrencies([...POPULAR_CURRENCIES])
    }
  }, [searchTerm])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  const handleSelect = (currency: string) => {
    onChange(currency)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
    } else if (event.key === 'Enter' && filteredCurrencies.length > 0) {
      handleSelect(filteredCurrencies[0])
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-white text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-3">
          {value ? (
            <>
              <span className="text-lg font-medium text-gray-600">
                {getCurrencySymbol(value)}
              </span>
              <span className="font-medium text-gray-900">{value}</span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
          isOpen ? 'transform rotate-180' : ''
        }`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search currencies..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Currency List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredCurrencies.length > 0 ? (
              filteredCurrencies.map((currency) => (
                <button
                  key={currency}
                  onClick={() => handleSelect(currency)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors ${
                    currency === value ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                  }`}
                  role="option"
                  aria-selected={currency === value}
                >
                  <span className="text-lg font-medium text-gray-600">
                    {getCurrencySymbol(currency)}
                  </span>
                  <span className="font-medium">{currency}</span>
                  {currency === value && (
                    <div className="ml-auto w-2 h-2 bg-primary-600 rounded-full"></div>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-gray-500 text-center">
                No currencies found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CurrencySelector